import { useParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { Badge, Banner, BilingualLabel, Card, CardBody, EmptyState } from '@/ui'
import { isRunLive, type PackRunView, type ProcessState } from '@/features/process/projection'
import { ProcessScreen } from '@/features/process/components/ProcessScreen'
import { SyncBadges } from '@/features/process/components/bits'
import { useDraftState } from '@/features/process/useProcess'
import { LotStateBadge } from '../components/LotStateBadge'
import { CoderCard } from './run/CoderCard'
import { StartSection } from './run/StartSection'
import { FillSection } from './run/FillSection'
import { CloseSection } from './run/CloseSection'
import { ClosedPackSection } from './run/ClosedPackSection'
import { emptyFill, type FillDraft } from './run/fillDraft'

/** One pack run at the line: start (F7.2), fill and weigh (F7.3), close with reconciliation (F7.4). */
export function PackRunPage() {
  const { runId = '' } = useParams()
  return (
    <ProcessScreen title="pack.run.title" backTo="/m/pack">
      {(state) => {
        const run = state.packRuns.get(runId)
        return run ? <PackRun key={run.id} state={state} run={run} /> : <Missing />
      }}
    </ProcessScreen>
  )
}

function Missing() {
  const { t } = useI18n()
  return <EmptyState title={t('pack.run.missing')} />
}

function PackRun({ state, run }: { state: ProcessState; run: PackRunView }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const actor = useActor()
  const [fill, update, loaded, clear] = useDraftState<FillDraft>(`pack.fill:${run.id}`, 'pack.fill', emptyFill, actor.user_id)
  if (!loaded) return null
  const lot = run.pack_lot ? (state.packLots.get(run.pack_lot.id) ?? run.pack_lot) : null
  const held = run.batch_hold !== 'NONE' || (lot ? lot.hold !== 'NONE' : false)
  const live = isRunLive(run.status) && !run.close
  const skuName = locale === 'hi' && run.sku.name_hi ? run.sku.name_hi : run.sku.name

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-1.5 py-3">
          <div className="text-[19px] font-bold leading-tight">{skuName}</div>
          <div className="font-mono text-[13px] text-muted">{run.code}</div>
          <div className="flex flex-wrap items-center gap-1.5 text-[13px]">
            <Badge>{run.batch_code}</Badge>
            {run.farmer_code && <span className="font-mono text-muted">{run.farmer_code}</span>}
            {lot && <LotStateBadge lot={lot} />}
            <SyncBadges sync={run.sync} />
          </div>
          <div className="text-[13px] text-muted tabular">
            {t('pack.plannedLine', { kg: fmt.weight(run.planned_g), n: run.planned_pouches })}
            {run.line_name ? ` · ${run.line_name}` : ''}
          </div>
        </CardBody>
      </Card>

      {held && live && (
        <Banner tone="error" title={<BilingualLabel k="pack.run.heldTitle" />}>
          {run.started_at ? t('pack.run.heldBodyStarted') : t('pack.run.heldBodyNotStarted')}
        </Banner>
      )}

      {!run.started_at || !lot ? (
        <StartSection state={state} run={run} blocked={held} />
      ) : live ? (
        <>
          <CoderCard lot={lot} />
          {fill.finished ? (
            <CloseSection run={run} fill={fill} update={update} onClosed={() => void clear()} />
          ) : (
            <FillSection state={state} run={run} fill={fill} update={update} held={held} />
          )}
        </>
      ) : (
        <ClosedPackSection state={state} run={run} />
      )}
    </div>
  )
}
